import React from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid';
import gql from 'graphql-tag';
import { Col, Row, Button } from '@storable/canaveral-components';
import { FieldArray } from 'formik';
import { object, string, array } from 'yup';
import { CONSUL, ROBOCERT, SSL_PROVIDERS } from '../../../constants/sslProviders';
import { validateDomain, validateWww } from '../../../modules/domain';
import { MANAGE } from '../../../modules/authorization/actions';
import { BUILDER } from '../../../modules/authorization/subjects';
import { FLIPPER_VOYAGER_ADMIN_DOMAIN_SSL_SETTINGS } from '../../../constants/flippers';
import useFlipper from '../../../modules/react/hooks/useFlipper';
import useCurrentUser from '../../hooks/useCurrentUser';
import withForm from '../../hocs/withForm';
import formatInitialValues from '../../lib/formatInitialValues';
import SettingsHeader from '../SettingsHeader';
import SettingsFooter from '../SettingsFooter';
import FormFieldLabel from '../FormFieldLabel';
import FormFieldText from '../FormFieldText';
import HostnameCertSettingsDomainDelete from '../HostnameCertSettingsDomainDelete';
import HostnameCertSettingsStatusPanel from '../HostnameCertSettingsStatusPanel';
import HostnameCertSettingsStatusIcons from '../HostnameCertSettingsStatusIcons';
import styles from './styles.scss';

const propTypes = {
  values: PropTypes.arrayOf(
    PropTypes.shape({
      hostname: PropTypes.string,
      id: PropTypes.string,
      primary: PropTypes.bool,
      ssl: PropTypes.bool.isRequired,
      sslType: PropTypes.oneOf(SSL_PROVIDERS).isRequired,
      websitePlatform: PropTypes.string,
      DNSChangesRequired: PropTypes.bool.isRequired,
      DNSRecord: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.string,
        type: PropTypes.string,
      }),
      certificate: PropTypes.shape({
        validTo: PropTypes.number,
      }),
    }).isRequired,
  ),
  onRequestSSL: PropTypes.func.isRequired,
  refetchHostnames: PropTypes.func.isRequired,
  setAlert: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  submitErrors: PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string,
  }),
};

const defaultProps = {
  values: [],
  submitErrors: [],
};

export const SETTINGS_TITLE = 'Domains & SSL Certificates';

const initHostname = primary => ({
  id: uuid.v4(),
  hostname: '',
  primary,
  ssl: false,
  sslType: CONSUL,
  websitePlatform: '',
  DNSChangesRequired: false,
  DNSRecord: {
    name: '',
    value: '',
    type: '',
  },
  certificate: {
    validTo: 0,
  },
});

const initialValues = {
  primaryHostname: initHostname(true),
  secondaryHostnames: [initHostname(false)],
};

const validationSchema = object().shape({
  primaryHostname: object().shape({
    id: string(),
    hostname: string()
      .test('is-primary-domain-valid', 'Primary domain is not valid', validateDomain)
      .test('is-primary-domain-www', 'Primary domain cannot start with `www.`', validateWww),
  }),
  secondaryHostnames: array()
    .of(object().shape({
      id: string(),
      hostname: string()
        .test('is-secondary-domain-valid', 'Secondary domain is not valid', validateDomain)
        .test('is-secondary-domain-www', 'Secondary domain cannot start with `www.`', validateWww),
    })),
});

const HostnameCertSettings = ({
  values,
  handleSubmit,
  handleReset,
  onRequestSSL,
  loading,
  refetchHostnames,
  setAlert,
  setFieldValue,
  submitting,
  submitErrors,
  submitted,
  hostnames,
}) => {
  const currentUser = useCurrentUser();
  const authorized = currentUser.can(MANAGE, BUILDER);
  const domainsSSLsEnabled = useFlipper(FLIPPER_VOYAGER_ADMIN_DOMAIN_SSL_SETTINGS);
  const authorizationErrors = [];
  const isSavedHostname = (hostname, position) => {
    const { primaryHostname, secondaryHostnames } = hostnames;
    return position === undefined
      ? primaryHostname?.hostname === hostname
      : secondaryHostnames[position]?.hostname === hostname;
  };

  if (!authorized) {
    authorizationErrors.push({ code: 0, message: 'You do not have permission to edit the site domains and SSLs.' });
  }

  if (!domainsSSLsEnabled) {
    authorizationErrors.push({ code: 0, message: 'Feature not enabled for current site.' });
  }

  const errors = [...authorizationErrors, ...submitErrors];

  const { primaryHostname, secondaryHostnames } = values;

  const handleSSLRequest = (hostname) => {
    onRequestSSL(hostname);
  };

  const handleDomainUpdate = (e) => {
    const { target: { name, value } } = e;
    const update = value ? value.toLowerCase() : '';
    setFieldValue(name, update);
  };

  const renderSecondaryHostnameFields = () => (
    <FieldArray name="secondaryHostnames">
      {({ push }) => (
        <>
          {secondaryHostnames.map((hostname, index) => (
            <div key={hostname.id} className={styles.secondaryHostnameRow}>
              <Row>
                <Col xs={3}>
                  <FormFieldText
                    label="Secondary Domain"
                    name={`secondaryHostnames.${index}.hostname`}
                    onChange={handleDomainUpdate}
                    hideLabel
                  />
                </Col>
                <Col xs={1} className={styles.trashButtonCol}>
                  <HostnameCertSettingsDomainDelete
                    domain={hostname.hostname}
                    hostnameId={hostname.id}
                    refetchHostnames={refetchHostnames}
                  />
                </Col>
                <Col xs={5} className={styles.statusIconsCol}>
                  <HostnameCertSettingsStatusIcons
                    ssl={hostname.ssl}
                    sslType={hostname.sslType}
                    // Adding null check here since our formatInitialValues() method doesn't
                    // currently support initializing objects nested in arrays
                    validTo={hostname.certificate?.validTo}
                    websitePlatform={hostname.websitePlatform}
                  />
                </Col>
              </Row>
              {isSavedHostname(hostname.hostname, index)
                && !(hostname.ssl && hostname.sslType === ROBOCERT && hostname.websitePlatform === 'voyager')
                && (
                  <Row>
                    <Col xs={5}>
                      <HostnameCertSettingsStatusPanel
                        hostname={hostname}
                        handleSSLRequest={() => handleSSLRequest(hostname)}
                      />
                    </Col>
                  </Row>
                )}
            </div>
          ))}
          <Button
            className={styles.button}
            context="neutral"
            icon="add"
            onClick={() => push(initHostname(false))}
            disabled={loading || !authorized}
          >
            Add Secondary Domain
          </Button>
        </>
      )}
    </FieldArray>
  );

  return (
    <form onSubmit={handleSubmit} onReset={handleReset}>
      <Row>
        <Col xs={12}>
          <SettingsHeader
            text={SETTINGS_TITLE}
            setAlert={setAlert}
          />
        </Col>
      </Row>
      {authorized && domainsSSLsEnabled && (
        <>
          <div className={styles.primaryHostnameRow}>
            <Row className={styles.secondaryHostnameLabelRow}>
              <Col xs={12}>
                <FormFieldLabel
                  title="Primary Domain"
                  tooltip={'Enter the domain without "www."\nFor example: storable.com'}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={3}>
                <FormFieldText
                  label="Primary Domain"
                  name={'primaryHostname.hostname'}
                  onChange={handleDomainUpdate}
                  hideLabel
                />
              </Col>
              <Col xs={1} className={styles.trashButtonCol}>
                <span>&nbsp;</span>
              </Col>
              <Col xs={5} className={styles.statusIconsCol}>
                <HostnameCertSettingsStatusIcons
                  ssl={primaryHostname.ssl}
                  sslType={primaryHostname.sslType}
                  validTo={primaryHostname.certificate.validTo}
                  websitePlatform={primaryHostname.websitePlatform}
                />
              </Col>
            </Row>
            {isSavedHostname(primaryHostname.hostname)
              && !(primaryHostname.ssl && primaryHostname.sslType === ROBOCERT)
              && (
                <Row>
                  <Col xs={5}>
                    <HostnameCertSettingsStatusPanel
                      hostname={primaryHostname}
                      handleSSLRequest={() => handleSSLRequest(primaryHostname)}
                    />
                  </Col>
                </Row>
              )}
          </div>
          <Row className={styles.secondaryHostnameLabelRow}>
            <Col xs={12}>
              <FormFieldLabel
                title="Secondary Domains"
                tooltip={'Enter the domain without "www."\nFor example: storable.com'}
              />
            </Col>
          </Row>
          {renderSecondaryHostnameFields()}
        </>
      )}
      <SettingsFooter
        disabled={!authorized}
        errors={errors}
        onCancel={handleReset}
        onSubmit={handleSubmit}
        submitting={submitting}
        success={submitted && !errors.length && 'Domains successfully saved'}
      />
    </form>
  );
};

HostnameCertSettings.propTypes = propTypes;
HostnameCertSettings.defaultProps = defaultProps;

HostnameCertSettings.fragments = {
  hostname: gql`
    fragment HostnameCertSettingsHostname on Hostname {
      hostname
      id
      primary
      ssl
      sslType
      certificate {
        ...HostnameCertSettingsStatusPanelCertificate
      }
      websitePlatform
    }

    ${HostnameCertSettingsStatusPanel.fragments.certificate}
  `,
  userError: gql`
    fragment HostnameCertSettingsUserError on UserError {
      code
      message
    }
  `,
};

export default withForm({
  mapPropsToValues: ({ hostnames }) => formatInitialValues(initialValues, hostnames),
  validationSchema,
  handleSubmit: (hostnames, formikBag) => formikBag.props.onSubmit(hostnames),
  enableReinitialize: true,
})(HostnameCertSettings);
